import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import Modal from '../../components/ui/Modal';
import api from '../../services/api';

// ─── Datas (texto YYYY-MM-DD, sem fuso) ──────────────────────────────────────
const DIAS = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];
const MESES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
const DIAS_LONGOS = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];

const paraData = (s) => new Date(`${s}T12:00:00Z`);
export const diaCurto = (s) => {
  const d = paraData(s);
  return `${DIAS[d.getUTCDay()]}, ${String(d.getUTCDate()).padStart(2, '0')}/${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};
export const diaLongo = (s) => {
  const d = paraData(s);
  return `${DIAS_LONGOS[d.getUTCDay()]}, ${d.getUTCDate()} de ${MESES[d.getUTCMonth()]}`;
};
const diferencaDias = (a, b) => Math.round((paraData(a) - paraData(b)) / 86400000);

export const rotuloPrazo = (prazo, hoje) => {
  const d = diferencaDias(prazo, hoje);
  if (d === 0) return { texto: 'vence hoje', cor: '#F59E0B' };
  if (d === 1) return { texto: 'vence amanhã', cor: null };
  if (d > 1) return { texto: `até ${diaCurto(prazo)}`, cor: null };
  if (d === -1) return { texto: 'venceu ontem', cor: '#EF4444' };
  return { texto: `venceu ${diaCurto(prazo)}`, cor: '#EF4444' };
};

export const saudacao = () => {
  const h = new Date().getHours();
  return h < 12 ? 'Bom dia' : h < 18 ? 'Boa tarde' : 'Boa noite';
};

// ─── Listas de pessoas e clientes (cache simples na sessão) ──────────────────
// Uma única busca por sessão, compartilhada por todos os formulários abertos
let buscaPessoas = null;
let buscaClientes = null;
export function useListas() {
  const [pessoas, setPessoas] = useState([]);
  const [clientes, setClientes] = useState([]);
  useEffect(() => {
    let ativo = true;
    buscaPessoas = buscaPessoas || api.get('/usuarios/equipe').then((r) => r.data || []).catch(() => { buscaPessoas = null; return []; });
    buscaClientes = buscaClientes || api.get('/clientes/lista').then((r) => r.data || []).catch(() => { buscaClientes = null; return []; });
    buscaPessoas.then((l) => ativo && setPessoas(l));
    buscaClientes.then((l) => ativo && setClientes(l));
    return () => { ativo = false; };
  }, []);
  return { pessoas, clientes };
}
export const nomeCliente = (c) => c.nome_fantasia || c.razao_social;
export const primeiroNome = (n) => (n || '').split(' ')[0];

// ─── Barra de progresso ──────────────────────────────────────────────────────
export function Barra({ feitas, total, altura = 8, t }) {
  const pct = total ? Math.round((feitas / total) * 100) : 0;
  return (
    <div style={{ height: altura, background: t.border, borderRadius: 999, overflow: 'hidden' }}>
      <div style={{ height: '100%', width: `${pct}%`, background: pct === 100 ? '#22C55E' : '#6C4CF5', borderRadius: 999, transition: 'width .3s' }} />
    </div>
  );
}

// ─── Linha de tarefa ─────────────────────────────────────────────────────────
export function LinhaTarefa({ tarefa: x, hoje, t, onConcluir, onPassar, onPrazo, onRemover, mostrarDono = false, compacta = false }) {
  const [aberta, setAberta] = useState(false);
  const feita = x.status === 'concluida';
  const prazo = rotuloPrazo(x.prazo, hoje);
  const detalhes = [];
  if (x.cliente_nome) detalhes.push(x.cliente_nome);
  if (mostrarDono && x.responsavel_nome) detalhes.push(primeiroNome(x.responsavel_nome));
  if (!feita) detalhes.push(<span key="p" style={{ color: prazo.cor || undefined, fontWeight: prazo.cor ? 600 : 400 }}>{prazo.texto}</span>);
  if (x.passada_de_nome) detalhes.push(`de ${primeiroNome(x.passada_de_nome)}`);
  else if (x.origem === 'demanda' && x.criado_por_nome && x.criado_por !== x.responsavel_id) detalhes.push(`pedido por ${primeiroNome(x.criado_por_nome)}`);

  return (
    <div style={{ padding: '11px 14px', borderTop: `1px solid ${t.border}` }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <input type="checkbox" checked={feita} onChange={() => onConcluir(x)} aria-label={feita ? 'Desmarcar' : 'Marcar como feita'}
          style={{ width: 19, height: 19, marginTop: 1, cursor: 'pointer', accentColor: '#22C55E', flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0, cursor: x.descricao || x.recado ? 'pointer' : 'default' }} onClick={() => setAberta((v) => !v)}>
          <p style={{ fontSize: 14, lineHeight: 1.4, color: feita ? t.textMuted : t.text, textDecoration: feita ? 'line-through' : 'none' }}>
            {x.titulo}
          </p>
          <p style={{ fontSize: 12, color: t.textMuted, marginTop: 2, display: 'flex', flexWrap: 'wrap', gap: '0 6px' }}>
            {detalhes.map((d, i) => <span key={i}>{i > 0 && '· '}{d}</span>)}
          </p>
          {aberta && (x.recado || x.descricao) && (
            <div style={{ marginTop: 6, fontSize: 13, color: t.textSecond, whiteSpace: 'pre-wrap' }}>
              {x.recado && <p>💬 "{x.recado}"</p>}
              {x.descricao && <p style={{ marginTop: x.recado ? 4 : 0 }}>{x.descricao}</p>}
            </div>
          )}
        </div>
        <span style={{
          fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 999, flexShrink: 0, marginTop: 2,
          color: x.passada_de ? '#F59E0B' : x.origem === 'rotina' ? '#A78BFA' : '#38BDF8',
          background: x.passada_de ? 'rgba(245,158,11,.12)' : x.origem === 'rotina' ? 'rgba(167,139,250,.12)' : 'rgba(56,189,248,.12)',
        }}>
          {x.passada_de ? 'recebida' : x.origem === 'rotina' ? 'rotina' : 'demanda'}
        </span>
      </div>
      {!feita && !compacta && (
        <div style={{ display: 'flex', gap: 14, marginTop: 6, paddingLeft: 31 }}>
          {[['Passar', () => onPassar(x)], ['Mudar prazo', () => onPrazo(x)], ['Remover', () => onRemover(x)]].map(([rotulo, acao]) => (
            <button key={rotulo} onClick={acao}
              style={{ fontSize: 12, color: rotulo === 'Remover' ? '#EF4444' : t.textSecond, background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
              {rotulo}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Nova demanda ────────────────────────────────────────────────────────────
export function ModalNovaDemanda({ aberto, onFechar, onCriada, usuarioAtual, hoje, paraQuem }) {
  const { pessoas, clientes } = useListas();
  const vazio = () => ({ titulo: '', descricao: '', responsavel_id: paraQuem || usuarioAtual?.id || '', prazo: hoje, cliente_id: '' });
  const [f, setF] = useState(vazio);
  const [salvando, setSalvando] = useState(false);
  useEffect(() => { if (aberto) setF(vazio()); }, [aberto]); // eslint-disable-line
  const s = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const salvar = async (e) => {
    e.preventDefault();
    setSalvando(true);
    try {
      await api.post('/pauta', { ...f, cliente_id: f.cliente_id || undefined });
      toast.success(f.responsavel_id === usuarioAtual?.id ? 'Adicionada à sua pauta.' : 'Demanda enviada.');
      onCriada();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Não foi possível criar a demanda.');
    } finally { setSalvando(false); }
  };

  return (
    <Modal open={aberto} onClose={onFechar} title="Nova demanda" size="md">
      <form onSubmit={salvar} className="space-y-3">
        <div>
          <label className="form-label">O que precisa ser feito *</label>
          <input className="form-input" value={f.titulo} onChange={(e) => s('titulo', e.target.value)} required autoFocus
            placeholder="Ex.: Arte do post de Dia das Crianças" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="form-label">Para quem</label>
            <select className="form-input" value={f.responsavel_id} onChange={(e) => s('responsavel_id', e.target.value)}>
              {pessoas.map((p) => <option key={p.id} value={p.id}>{p.id === usuarioAtual?.id ? 'Para mim' : p.nome}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label">Até quando</label>
            <input type="date" className="form-input" value={f.prazo} min={hoje} onChange={(e) => s('prazo', e.target.value)} required />
          </div>
        </div>
        <div>
          <label className="form-label">Cliente</label>
          <select className="form-input" value={f.cliente_id} onChange={(e) => s('cliente_id', e.target.value)}>
            <option value="">Nenhum / interno</option>
            {clientes.map((c) => <option key={c.id} value={c.id}>{nomeCliente(c)}</option>)}
          </select>
        </div>
        <div>
          <label className="form-label">Detalhes</label>
          <textarea className="form-input" rows={3} value={f.descricao} onChange={(e) => s('descricao', e.target.value)}
            placeholder="Links, referências, o que for preciso (opcional)" />
        </div>
        <div className="flex gap-2 pt-1">
          <button className="btn-primary flex-1" disabled={salvando}>{salvando ? 'Salvando...' : 'Adicionar'}</button>
          <button type="button" className="btn-secondary" onClick={onFechar}>Cancelar</button>
        </div>
      </form>
    </Modal>
  );
}

// ─── Passar para outra pessoa ────────────────────────────────────────────────
export function ModalPassar({ tarefa, onFechar, onPassada }) {
  const { pessoas } = useListas();
  const [para, setPara] = useState('');
  const [recado, setRecado] = useState('');
  const [salvando, setSalvando] = useState(false);
  useEffect(() => { setPara(''); setRecado(''); }, [tarefa]);
  if (!tarefa) return null;
  const opcoes = pessoas.filter((p) => p.id !== tarefa.responsavel_id);

  const passar = async (e) => {
    e.preventDefault();
    if (!para) return toast.error('Escolha para quem passar.');
    setSalvando(true);
    try {
      await api.post(`/pauta/${tarefa.id}/passar`, { para_id: para, recado });
      toast.success(`Passada para ${primeiroNome(pessoas.find((p) => p.id === para)?.nome)}.`);
      onPassada();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Não foi possível passar a tarefa.');
    } finally { setSalvando(false); }
  };

  return (
    <Modal open onClose={onFechar} title="Passar para outra pessoa" size="sm">
      <form onSubmit={passar} className="space-y-3">
        <p className="text-sm" style={{ opacity: 0.8 }}>{tarefa.titulo}</p>
        <div>
          <label className="form-label">Para quem</label>
          <select className="form-input" value={para} onChange={(e) => setPara(e.target.value)} required>
            <option value="">Escolha…</option>
            {opcoes.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
          </select>
        </div>
        <div>
          <label className="form-label">Recado</label>
          <textarea className="form-input" rows={2} value={recado} onChange={(e) => setRecado(e.target.value)}
            placeholder="Ex.: separei os 12 takes na pasta do cliente" />
        </div>
        <div className="flex gap-2 pt-1">
          <button className="btn-primary flex-1" disabled={salvando}>{salvando ? 'Passando...' : 'Passar'}</button>
          <button type="button" className="btn-secondary" onClick={onFechar}>Cancelar</button>
        </div>
      </form>
    </Modal>
  );
}

// ─── Mudar prazo ─────────────────────────────────────────────────────────────
export function ModalPrazo({ tarefa, hoje, onFechar, onSalvo }) {
  const [prazo, setPrazo] = useState('');
  useEffect(() => { if (tarefa) setPrazo(tarefa.prazo < hoje ? hoje : tarefa.prazo); }, [tarefa, hoje]);
  if (!tarefa) return null;
  const salvar = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/pauta/${tarefa.id}`, { prazo });
      toast.success('Prazo atualizado.');
      onSalvo();
    } catch (err) { toast.error(err.response?.data?.error || 'Não foi possível mudar o prazo.'); }
  };
  return (
    <Modal open onClose={onFechar} title="Mudar prazo" size="sm">
      <form onSubmit={salvar} className="space-y-3">
        <p className="text-sm" style={{ opacity: 0.8 }}>{tarefa.titulo}</p>
        <input type="date" className="form-input" value={prazo} onChange={(e) => setPrazo(e.target.value)} required />
        <div className="flex gap-2 pt-1">
          <button className="btn-primary flex-1">Salvar</button>
          <button type="button" className="btn-secondary" onClick={onFechar}>Cancelar</button>
        </div>
      </form>
    </Modal>
  );
}
